// Bulletproof scroll positioning utility for Guide pages
// Accounts for all dynamic elements that affect positioning

export interface ScrollPositionConfig {
  targetElementId: string;
  forceScrollBehavior?: 'instant' | 'smooth';
  skipProgressBarCheck?: boolean;
  debugMode?: boolean;
}

export interface ScrollPositionResult {
  success: boolean;
  targetPosition: number;
  actualOffset: number;
  debugInfo?: {
    navHeight: number;
    progressBarHeight: number;
    progressBarVisible: boolean;
    elementFound: boolean;
    elementTop: number;
    buffersApplied: number[];
  };
}

/**
 * Calculate the exact scroll offset needed based on current page state
 */
function calculateDynamicOffset(): {
  offset: number;
  debugInfo: {
    navHeight: number;
    progressBarHeight: number;
    progressBarVisible: boolean;
    buffersApplied: number[];
  };
} {
  // Navigation bar is always present and sticky (h-16 = 64px)
  const navHeight = 64;
  
  // Check if reading progress bar is currently visible
  const progressBar = document.querySelector('.fixed.top-16[class*="z-40"]');
  const progressBarVisible = !!(progressBar && window.scrollY > 200);
  const progressBarHeight = progressBarVisible ? 48 : 0; // h-12 = 48px when visible
  
  // Apply safety buffers for edge cases
  const buffers: number[] = [];
  
  // Buffer for any layout shift during scroll
  buffers.push(8);
  
  // BREATHING ROOM: Add 15px padding below progress bar for better UX
  buffers.push(15);
  
  // Additional buffer if we're near the progress bar threshold (190-210px)
  if (window.scrollY >= 190 && window.scrollY <= 210) {
    buffers.push(12); // Extra buffer during transition zone
  }
  
  const totalBuffers = buffers.reduce((sum, buffer) => sum + buffer, 0);
  const offset = navHeight + progressBarHeight + totalBuffers;
  
  return {
    offset,
    debugInfo: {
      navHeight,
      progressBarHeight,
      progressBarVisible,
      buffersApplied: buffers,
    },
  };
}

/**
 * Wait for element to exist and have proper positioning
 */
async function waitForElement(
  elementId: string,
  maxWaitMs: number = 2000
): Promise<HTMLElement | null> {
  const startTime = Date.now();
  
  while (Date.now() - startTime < maxWaitMs) {
    const element = document.getElementById(elementId);
    if (element) {
      // Ensure element has proper positioning (not 0,0)
      const rect = element.getBoundingClientRect();
      if (rect.top !== 0 || rect.left !== 0) {
        return element;
      }
    }
    
    // Wait 16ms (60fps) before next check
    await new Promise(resolve => setTimeout(resolve, 16));
  }
  
  return null;
}

/**
 * Block all other scroll operations during positioning
 */
let scrollLock = false;
const scrollQueue: Array<() => void> = [];

function withScrollLock<T>(fn: () => Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const execute = async () => {
      if (scrollLock) {
        scrollQueue.push(() => execute());
        return;
      }
      
      scrollLock = true;
      try {
        const result = await fn();
        resolve(result);
      } catch (error) {
        reject(error);
      } finally {
        scrollLock = false;
        
        // Process next queued scroll
        const next = scrollQueue.shift();
        if (next) {
          setTimeout(next, 10);
        }
      }
    };
    
    execute();
  });
}

/**
 * Disable bookmark restoration temporarily
 */
function disableBookmarkRestoration(durationMs: number = 2000) {
  const originalScrollTo = window.scrollTo;
  let restoreDisabled = true;
  
  // Override window.scrollTo to block bookmark scrolls
  window.scrollTo = function(...args: any[]) {
    const options = args[0];
    if (
      restoreDisabled &&
      options &&
      typeof options === 'object' &&
      options.behavior === 'smooth' &&
      args.length === 1
    ) {
      // This looks like a bookmark restoration - block it
      console.log('[ScrollPositioning] Blocked bookmark restoration during user navigation');
      return;
    }
    
    // Allow other scrolls
    // @ts-ignore - Handle both overloads of scrollTo
    originalScrollTo.apply(window, args);
  };
  
  // Restore normal scrollTo after delay
  setTimeout(() => {
    restoreDisabled = false;
    window.scrollTo = originalScrollTo;
  }, durationMs);
}

/**
 * Bulletproof scroll to element with all edge cases handled
 */
export async function scrollToElement(
  config: ScrollPositionConfig
): Promise<ScrollPositionResult> {
  const { targetElementId, forceScrollBehavior = 'smooth', skipProgressBarCheck = false, debugMode = false } = config;
  
  return withScrollLock(async () => {
    // Disable any competing scroll operations
    disableBookmarkRestoration(2000);
    
    // Dispatch global event to block intersection observer
    window.dispatchEvent(new CustomEvent('toc-click', { detail: { targetId: targetElementId } }));
    
    // Wait for element to be ready
    const element = await waitForElement(targetElementId);
    if (!element) {
      return {
        success: false,
        targetPosition: 0,
        actualOffset: 0,
        debugInfo: debugMode ? {
          navHeight: 64,
          progressBarHeight: 0,
          progressBarVisible: false,
          elementFound: false,
          elementTop: 0,
          buffersApplied: [],
        } : undefined,
      };
    }
    
    // Wait a frame to ensure layout is stable
    await new Promise(resolve => requestAnimationFrame(resolve));
    
    // Calculate current dynamic offset
    const { offset, debugInfo } = calculateDynamicOffset();
    
    // Get element's absolute position (NOT relative to viewport)
    const elementRect = element.getBoundingClientRect();
    const absoluteElementTop = elementRect.top + window.pageYOffset;
    const targetPosition = Math.max(0, absoluteElementTop - offset);
    
    // Perform the scroll
    window.scrollTo({
      top: targetPosition,
      behavior: forceScrollBehavior,
    });
    
    // Verify scroll completed correctly (for smooth scrolls)
    if (forceScrollBehavior === 'smooth') {
      // Wait for scroll to complete, then verify position
      setTimeout(() => {
        const currentScroll = window.pageYOffset;
        const difference = Math.abs(currentScroll - targetPosition);
        
        // If we're off by more than 20px, do an instant correction
        if (difference > 20) {
          console.log(`[ScrollPositioning] Correcting position: expected ${targetPosition}, got ${currentScroll}`);
          window.scrollTo({
            top: targetPosition,
            behavior: 'instant',
          });
        }
      }, 800); // Allow time for smooth scroll to complete
    }
    
    return {
      success: true,
      targetPosition,
      actualOffset: offset,
      debugInfo: debugMode ? {
        ...debugInfo,
        elementFound: true,
        elementTop: absoluteElementTop,
      } : undefined,
    };
  });
}

/**
 * Initialize scroll positioning system
 */
export function initializeScrollPositioning() {
  // Block automatic bookmark restoration on page load
  // Let user navigation take precedence for first 3 seconds
  disableBookmarkRestoration(3000);
  
  if (typeof window !== 'undefined') {
    console.log('[ScrollPositioning] Bulletproof scroll positioning initialized');
  }
}